import * as React from 'react';
import cn from 'classnames';
import Icon from '@brighthr/component-icon';

export type CheckboxProps = React.InputHTMLAttributes<HTMLInputElement> & {
	label?: string;
	description?: string;
	checkboxButton?: boolean;
	checkboxPosition?: 'left' | 'right';
	circularCheckbox?: boolean;
	iconName?: string;
	checkboxButtonSize?: 'sm' | 'base';
};

export type InputProps = {
	label?: string;
	checked: boolean;
	disabled?: boolean;
	circularCheckbox?: boolean;
	checkboxPosition?: 'left' | 'right';
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
	(
		{
			checked,
			disabled,
			label,
			circularCheckbox,
			checkboxPosition,

			...props
		},
		ref
	) => (
		<>
			<input
				data-testid={`input${
					checkboxPosition === 'left' ? 'Left' : 'Right'
				}`}
				checked={checked}
				disabled={disabled}
				type="checkbox"
				className="appearance-none peer/checkbox"
				ref={ref}
				{...props}
			/>
			<span
				className={cn(
					'border relative inline-flex items-center justify-center w-6 h-6 p-1 transition-colors ease-in-out',
					{
						'mr-4': Boolean(label),
						'bg-white group-hover/checkbox:border-primary-600':
							!checked && !disabled,
						'bg-neutral-100': !checked && disabled,
						'bg-primary-400': checked && !disabled,
						'bg-neutral-300': checked && disabled,
						'border-primary-400 cursor-pointer peer-focus/checkbox:outline peer-focus/checkbox:outline-primary-600':
							!disabled,
						'border-neutral-300 cursor-not-allowed': disabled,
						rounded: !circularCheckbox,
						'rounded-full': circularCheckbox,
					}
				)}
				aria-hidden="true"
			>
				<Icon
					iconName="tick-thick"
					className={cn('transition-colors ease-in-out', {
						'fill-white': checked,
						'fill-transparent': !checked,
					})}
				/>
			</span>
		</>
	)
);
const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
	(
		{
			label,
			checked,
			disabled,
			description,
			checkboxButton = false,
			checkboxPosition = 'left',
			iconName,
			checkboxButtonSize = 'base',
			...props
		},
		ref
	) => (
		<label
			data-testid="checkboxLabel"
			className={cn('flex group/checkbox ', {
				'border rounded-lg transition-colors ease-in-out':
					checkboxButton,
				'border rounded-lg border-transparent p-0.5 ease-in-out':
					!checkboxButton,
				'cursor-pointer': !disabled,
				'cursor-not-allowed': disabled,
				'border-gray-300 hover:border-gray-400':
					checkboxButton && !disabled && !checked,
				'border-primary-400': checkboxButton && !disabled && checked,
				'border-neutral-300': checkboxButton && disabled,
				'bg-white': checkboxButton && !checked,
				'bg-primary-50': checkboxButton && checked && !disabled,
				'bg-neutral-100': checkboxButton && checked && disabled,
				'justify-between': checkboxPosition === 'right',
				'items-center': !description,
				'p-2': checkboxButtonSize === 'sm' && checkboxButton,
				'p-4': checkboxButtonSize === 'base' && checkboxButton,
			})}
		>
			{checkboxPosition === 'left' && (
				<Input
					checked={checked}
					disabled={disabled}
					label={label}
					ref={ref}
					{...props}
				/>
			)}
			<div
				className={cn('flex space-x-3 ', {
					'items-center': !description,
				})}
			>
				<div>
					{iconName && (
						<Icon
							data-testid="inputIcon"
							size={30}
							iconName={iconName}
							className={cn({
								'fill-neutral-500':
									(!checked && disabled) ||
									(checked && disabled),
							})}
						/>
					)}
				</div>
				<div className="flex flex-col">
					<span
						className={cn('text-base font-semibold', {
							'mb-2': description,

							'text-neutral-700': !disabled,
							'text-neutral-500': disabled,
						})}
					>
						{label}
					</span>
					{description && (
						<span
							className={cn('text-sm font-normal', {
								'text-neutral-600': !disabled,
								'text-neutral-500': disabled,
							})}
						>
							{description}
						</span>
					)}
				</div>
			</div>
			{checkboxPosition === 'right' && (
				<Input
					checked={checked}
					disabled={disabled}
					label={label}
					ref={ref}
					{...props}
				/>
			)}
		</label>
	)
);

export default Checkbox;
